// Interpretation rules for the four Able Assess falls-risk tests, taken from
// Able Care's "Multifactorial Evidence Base for Falls Risk Screening" (Oct 2025).
//
// Able sends its own `at_risk` flag on every measurement and that flag is the
// source of truth. These rules only add the banding and threshold the app shows
// next to a result, so a caregiver can see *why* a test was flagged.

export const KG_TO_LBS = 2.20462;

// CDC STEADI 30-second Chair Stand cut-offs: [minAge, maxAge, belowAvgUnder, aboveAvgOver].
// "Average" is the inclusive range between the two.
const CST_TABLE = {
  male: [
    [60, 64, 14, 19],
    [65, 69, 12, 18],
    [70, 74, 12, 17],
    [75, 79, 11, 17],
    [80, 84, 10, 15],
    [85, 89, 8, 14],
    [90, 94, 7, 12],
  ],
  female: [
    [60, 64, 12, 17],
    [65, 69, 11, 16],
    [70, 74, 10, 15],
    [75, 79, 10, 15],
    [80, 84, 9, 14],
    [85, 89, 8, 13],
    [90, 94, 4, 11],
  ],
};

export const TUG_CUTOFF_SEC = 12; // STEADI: >= 12 s is Below Normal
export const GAIT_CUTOFF_MS = 0.8; // WGFP: < 0.8 m/s is Below Normal

export const normaliseSex = (gender) => {
  const g = String(gender || "").trim().toLowerCase();
  if (g === "male" || g === "m") return "male";
  if (g === "female" || g === "f") return "female";
  return null;
};

export const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
};

export const cstBand = (age, sex) => {
  if (age == null || !sex) return null;
  const row = (CST_TABLE[sex] || []).find(([lo, hi]) => age >= lo && age <= hi);
  if (!row) return null;
  return { minAge: row[0], maxAge: row[1], below: row[2], above: row[3] };
};

// Each interpreter returns { status, label, note, scale } or a reason the test
// could not be scored. `status` is "ok" | "risk" | "good" | null.
export function interpretCst(reps, age, sex) {
  if (age != null && age < 60) {
    return { status: null, label: "Not scored", note: "STEADI scores adults 60+ only" };
  }
  const band = cstBand(age, sex);
  if (!band) {
    return { status: null, label: null, note: "Needs client age (60–94) and sex for STEADI banding" };
  }
  const range = `${sex === "male" ? "Men" : "Women"} ${band.minAge}–${band.maxAge}`;
  const scale = {
    min: 0,
    max: Math.max(band.above + 6, (reps || 0) + 2),
    segments: [
      { to: band.below, tone: "risk" },
      { to: band.above + 1, tone: "ok" },
      { to: Infinity, tone: "good" },
    ],
    ticks: [band.below, band.above],
  };
  if (reps == null) return { status: null, label: null, note: range, scale };
  if (reps < band.below) {
    return { status: "risk", label: "Below average", note: `${range}: average is ${band.below}–${band.above}`, scale };
  }
  if (reps > band.above) {
    return { status: "good", label: "Above average", note: `${range}: average is ${band.below}–${band.above}`, scale };
  }
  return { status: "ok", label: "Average", note: `${range}: average is ${band.below}–${band.above}`, scale };
}

export function interpretTug(sec, age) {
  if (age != null && age < 65) {
    return { status: null, label: "Not scored", note: "STEADI scores adults 65+ only" };
  }
  const scale = {
    min: 0,
    max: Math.max(24, (sec || 0) + 2),
    segments: [
      { to: TUG_CUTOFF_SEC, tone: "good" },
      { to: Infinity, tone: "risk" },
    ],
    ticks: [TUG_CUTOFF_SEC],
  };
  const note = `Threshold ${TUG_CUTOFF_SEC} s (CDC STEADI)`;
  if (sec == null) return { status: null, label: null, note, scale };
  return sec < TUG_CUTOFF_SEC
    ? { status: "good", label: "Normal", note, scale }
    : { status: "risk", label: "Below normal", note, scale };
}

export function interpretGait(ms, age) {
  if (age != null && age < 65) {
    return { status: null, label: "Not scored", note: "Scored for adults 65+ only" };
  }
  const scale = {
    min: 0,
    max: Math.max(1.6, (ms || 0) + 0.2),
    segments: [
      { to: GAIT_CUTOFF_MS, tone: "risk" },
      { to: Infinity, tone: "good" },
    ],
    ticks: [GAIT_CUTOFF_MS],
  };
  const note = `Threshold ${GAIT_CUTOFF_MS} m/s (WGFP)`;
  if (ms == null) return { status: null, label: null, note, scale };
  return ms >= GAIT_CUTOFF_MS
    ? { status: "good", label: "Normal", note, scale }
    : { status: "risk", label: "Below normal", note, scale };
}

// Grip is benchmarked by Able against its own (unpublished) normative percentile
// model, so there is no local threshold — only Able's flag.
export function interpretGrip() {
  return { status: null, label: null, note: "Benchmarked by Able Care against age- and sex-matched norms" };
}
